import { Form, Input, Select } from "antd"
import { useCallback, useEffect, useState } from "react"
import { t } from "~/languages/i18n"
import { fetchPagesForSelector, fetchPostsForSelector } from "~/modules/post/services/post"
import { ParentQuestionFieldType, ParentQuestionFormProps } from "~/types/types"

const removeAccents = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[âÂ]/g, "a")
    .replace(/[êÊ]/g, "e")
    .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
}

interface LinkedItem {
  id: number
  name: string
}

const STATUS_OPTIONS = [
  { value: "PENDING", label: t("Pending") },
  { value: "APPROVED", label: t("Approved") },
  { value: "REJECTED", label: t("Rejected") },
]

const ParentQuestionForm: React.FC<ParentQuestionFormProps> = ({
  form,
  onFinishFailed,
  onFinish,
  initialSlug,
}) => {
  const [posts, setPosts] = useState<LinkedItem[]>([])
  const [pages, setPages] = useState<LinkedItem[]>([])
  const [postLoading, setPostLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(false)
  const [postPage, setPostPage] = useState(1)
  const [pagePage, setPagePage] = useState(1)
  const [hasMorePosts, setHasMorePosts] = useState(true)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [postSearch, setPostSearch] = useState("")
  const [pageSearch, setPageSearch] = useState("")

  const fetchPostsData = useCallback(async (search: string, page: number, append: boolean = false) => {
    setPostLoading(true)
    try {
      const response = await fetchPostsForSelector({ search, page })
      const newPosts = response.data || []
      setPosts(prev => append ? [...prev, ...newPosts] : newPosts)
      const totalPages = response.meta?.pagination?.pageCount || 1
      setHasMorePosts(page < totalPages)
      setPostPage(page)
    } catch (error) {
      console.error("Failed to fetch posts:", error)
    } finally {
      setPostLoading(false)
    }
  }, [])

  const fetchPagesData = useCallback(async (search: string, page: number, append: boolean = false) => {
    setPageLoading(true)
    try {
      const response = await fetchPagesForSelector({ search, page })
      const newPages = response.data || []
      setPages(prev => append ? [...prev, ...newPages] : newPages)
      const totalPages = response.meta?.pagination?.pageCount || 1
      setHasMorePages(page < totalPages)
      setPagePage(page)
    } catch (error) {
      console.error("Failed to fetch pages:", error)
    } finally {
      setPageLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPostsData("", 1)
    fetchPagesData("", 1)
  }, [fetchPostsData, fetchPagesData])

  const handlePostSearch = (value: string) => {
    setPostSearch(value)
    fetchPostsData(value, 1, false)
  }

  const handlePageSearch = (value: string) => {
    setPageSearch(value)
    fetchPagesData(value, 1, false)
  }

  const handlePostPopupScroll = (e: React.UIEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10 && hasMorePosts && !postLoading) {
      fetchPostsData(postSearch, postPage + 1, true)
    }
  }

  const handlePagePopupScroll = (e: React.UIEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10 && hasMorePages && !pageLoading) {
      fetchPagesData(pageSearch, pagePage + 1, true)
    }
  }

  const handleSubmit = () => {
    const formData = form.getFieldsValue()
    onFinish(formData)
  }

  const handleQuestionBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const questionValue = e.target.value.trim()
    if (!questionValue) {
      form.setFields([{ name: "question", errors: [] }])
      return
    }

    const slugValue = removeAccents(questionValue)
    form.setFieldsValue({ slug: slugValue })
  }

  return (
    <Form
      form={form}
      name="parent-question-form"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
      className="flex flex-col w-[70%]"
    >
      <Form.Item<ParentQuestionFieldType>
        name="question"
        rules={[{ required: true, message: t("Please enter Parent Question!") }]}
        label={<span className="text-base font-medium">{t("Question")}</span>}
        className="mt-4"
      >
        <Input
          placeholder={t("Enter Parent Question")}
          className="custom-input py-2"
          onBlur={handleQuestionBlur}
        />
      </Form.Item>

      <Form.Item<ParentQuestionFieldType>
        name="answer"
        rules={[{ required: true, message: t("Please enter Parent Question answer!") }]}
        label={<span className="text-base font-medium">{t("Answer")}</span>}
      >
        <Input.TextArea
          placeholder={t("Enter Parent Question answer")}
          className="custom-input py-2"
          rows={8}
        />
      </Form.Item>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item<ParentQuestionFieldType>
          name="author_name"
          label={<span className="text-base font-medium">{t("Name")}</span>}
        >
          <Input placeholder={t("Enter name")} className="custom-input py-2" />
        </Form.Item>

        <Form.Item<ParentQuestionFieldType>
          name="email"
          label={<span className="text-base font-medium">{t("Email")}</span>}
          rules={[
            { type: "email", message: t("Please enter a valid email!") },
          ]}
        >
          <Input placeholder={t("user@example.com")} className="custom-input py-2" />
        </Form.Item>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item<ParentQuestionFieldType>
          name="phone"
          label={<span className="text-base font-medium">{t("Phone")}</span>}
        >
          <Input placeholder={t("Enter phone")} className="custom-input py-2" />
        </Form.Item>

        <Form.Item<ParentQuestionFieldType>
          name="status"
          label={<span className="text-base font-medium">{t("Status")}</span>}
          initialValue="PENDING"
        >
          <Select placeholder={t("Select status")} options={STATUS_OPTIONS} />
        </Form.Item>
      </div>

      <div className="flex gap-4">
        <Form.Item<ParentQuestionFieldType>
          name="post"
          label={<span className="text-base font-medium">{t("Link to Post")}</span>}
          className="w-1/2"
        >
          <Select
            showSearch
            placeholder={t("Select a Post")}
            allowClear
            loading={postLoading}
            options={posts.map((post: LinkedItem) => ({
              value: post.id,
              label: post.name,
            }))}
            onSearch={handlePostSearch}
            onPopupScroll={handlePostPopupScroll}
            filterOption={false}
            notFoundContent={postLoading ? "Loading..." : "No results"}
          />
        </Form.Item>

        <Form.Item<ParentQuestionFieldType>
          name="page"
          label={<span className="text-base font-medium">{t("Link to Page")}</span>}
          className="w-1/2"
        >
          <Select
            showSearch
            placeholder={t("Select a Page")}
            allowClear
            loading={pageLoading}
            options={pages.map((page: LinkedItem) => ({
              value: page.id,
              label: page.name,
            }))}
            onSearch={handlePageSearch}
            onPopupScroll={handlePagePopupScroll}
            filterOption={false}
            notFoundContent={pageLoading ? "Loading..." : "No results"}
          />
        </Form.Item>
      </div>
    </Form>
  )
}

export default ParentQuestionForm
